import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// Note: This component uses mock data and needs to be updated for Firebase integration
// Placeholder interfaces and functions
interface ScoutingRun { 
  id: string; 
  name: string; 
  description: string; 
  startDate: string; 
  endDate: string; 
  locationIds: string[]; 
  traitIds: string[]; 
  assigneeIds: string[]; 
  status: "planned" | "in-progress" | "completed";
  crop?: string;
}
const mockTeam: any[] = [];
const mockTraits: any[] = [];
const locations: any[] = [];
const getFarmById = (id: string) => null;
const getFieldById = (id: string) => null;
const getGrowerById = (id: string) => null;
import { format } from "date-fns";
import { Calendar, Edit, MapPin, MoreHorizontal, Trash2, Users } from "lucide-react";
import React from "react";

interface ScoutingRunCardProps {
  scoutingRun: ScoutingRun;
  onEdit: (scoutingRunId: string) => void;
  onDelete: (scoutingRunId: string) => void;
  onView: (scoutingRunId: string) => void;
}

const ScoutingRunCard: React.FC<ScoutingRunCardProps> = ({ scoutingRun, onEdit, onDelete, onView }) => {
  const traits = scoutingRun.traitIds.map((id) => mockTraits.find((trait) => trait.id === id)).filter(Boolean);

  const assignees = scoutingRun.assigneeIds.map((id) => mockTeam.find((member) => member.id === id)).filter(Boolean);

  const locationDetails = scoutingRun.locationIds
    .map((id) => {
      const location = locations.find((loc) => loc.id === id);
      if (!location) return null;

      const field = getFieldById(location.fieldId);
      const farm = getFarmById(location.farmId);
      const grower = getGrowerById(location.growerId);

      return {
        fieldName: field?.name || "Unknown Field",
        farmName: farm?.name || "Unknown Farm",
        growerName: grower?.name || "Unknown Grower",
      };
    })
    .filter(Boolean);

  const statusColor = {
    planned: "bg-blue-100 text-blue-800 hover:bg-blue-100",
    "in-progress": "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
    completed: "bg-green-100 text-green-800 hover:bg-green-100",
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger view if clicking on the menu
    if ((e.target as HTMLElement).closest('[data-dropdown-menu-trigger="true"]')) {
      return;
    }
    onView(scoutingRun.id);
  };

  return (
    <Card
      className="w-full cursor-pointer relative"
      onClick={handleCardClick}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-lg text-agri-green-dark">{scoutingRun.name}</CardTitle>
            <CardDescription>{scoutingRun.description}</CardDescription>
          </div>
          <div className="absolute top-2 right-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="h-8 w-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none"
                  onClick={(e) => e.stopPropagation()}
                  data-dropdown-menu-trigger="true">
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-32">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(scoutingRun.id);
                  }}
                  className="cursor-pointer">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(scoutingRun.id);
                  }}
                  className="text-red-600 focus:text-red-600 cursor-pointer">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center justify-between mb-1">
          {scoutingRun.crop && (
            <Badge
              variant="outline"
              className="text-primary border-primary">
              {scoutingRun.crop}
            </Badge>
          )}
          <Badge
            className={statusColor[scoutingRun.status]}
            variant="outline">
            {scoutingRun.status.replace("-", " ")}
          </Badge>
        </div>

        <div className="flex items-center text-sm">
          <Calendar className="h-4 w-4 mr-2 text-gray-500" />
          {format(new Date(scoutingRun.startDate), "MMM d, yyyy")} -{" "}
          {format(new Date(scoutingRun.endDate), "MMM d, yyyy")}
        </div>

        <div className="flex items-center text-sm">
          <Users className="h-4 w-4 mr-2 text-gray-500" />
          {assignees.length > 0
            ? assignees.map((a) => (a ? `${a.firstName} ${a.lastName}` : "")).join(", ")
            : "No assignees"}
        </div>

        <div className="flex items-start text-sm">
          <MapPin className="h-4 w-4 mr-2 text-gray-500 mt-1" />
          <div>
            {locationDetails.length > 0 ? (
              <ul>
                {locationDetails.slice(0, 2).map((loc, index) => (
                  <li key={index}>
                    {loc?.fieldName} ({loc?.farmName})
                  </li>
                ))}
                {locationDetails.length > 2 && <li>+{locationDetails.length - 2} more locations</li>}
              </ul>
            ) : (
              "No locations"
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mt-2">
          {traits.map((trait) => (
            <Badge
              key={trait?.id}
              variant="outline">
              {trait?.name}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ScoutingRunCard;
